import React, { useState } from 'react'
import { FileText, Check, Eye, X } from 'lucide-react'
import { DEFAULT_NDA_TEMPLATES } from '../lib/ndaTemplates'
import ReactMarkdown from 'react-markdown'

interface NdaTemplateSelectorProps {
  currentText: string
  currentTitle: string
  onSelect: (title: string, text: string) => void
}

const NdaTemplateSelector: React.FC<NdaTemplateSelectorProps> = ({
  currentText,
  currentTitle,
  onSelect
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null)
  const [previewTemplate, setPreviewTemplate] = useState<number | null>(null)

  const handleSelectTemplate = (index: number) => {
    const template = DEFAULT_NDA_TEMPLATES[index]
    onSelect(template.title, template.text)
    setSelectedTemplate(index)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">NDA Templates</h4>
          <p className="text-xs text-gray-600 mt-1">
            Choose from pre-built templates or use your custom text
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {DEFAULT_NDA_TEMPLATES.map((template, index) => (
          <div
            key={index}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedTemplate === index
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <h5 className="font-medium text-gray-900">{template.name}</h5>
                  {template.isDefault && (
                    <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                      Default
                    </span>
                  )}
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded capitalize">
                    {template.type}
                  </span>
                  {template.industry && template.industry !== 'general' && (
                    <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded capitalize">
                      {template.industry}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{template.description}</p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setPreviewTemplate(index)
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                  title="Preview template"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleSelectTemplate(index)}
                  className={`px-4 py-2 rounded font-medium text-sm transition-colors ${
                    selectedTemplate === index
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {selectedTemplate === index ? (
                    <>
                      <Check className="h-4 w-4 inline mr-1" />
                      Selected
                    </>
                  ) : (
                    'Use Template'
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Template Preview Modal */}
      {previewTemplate !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-full sm:max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {DEFAULT_NDA_TEMPLATES[previewTemplate].name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {DEFAULT_NDA_TEMPLATES[previewTemplate].description}
                </p>
              </div>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown>{DEFAULT_NDA_TEMPLATES[previewTemplate].text}</ReactMarkdown>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setPreviewTemplate(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleSelectTemplate(previewTemplate)
                  setPreviewTemplate(null)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Use This Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NdaTemplateSelector
